/**
 * Client mirror of the server-side artifact style guide.
 *
 * Presentation slides are stored as bare `<section>` elements; every place
 * that wraps them in an iframe (canvas, library tile, preview dialog,
 * inline preview card) needs to include the same Manrope/Sora font link
 * and enterprise base CSS so the rendered output matches an HTML artifact
 * that ships the prelude inside its own `<head>`.
 *
 * Keep this file byte-identical to
 * `packages/ai/src/artifacts/style-guide.ts` for the constants below — if
 * either side changes, change both.
 *
 * @module lib/artifacts/style-guide
 */

/** Google Fonts `<link>` for Manrope (body) + Sora (headings). */
export const ARTIFACT_FONT_LINK_HTML = `<!-- taqat-artifact-fonts -->
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Sora:wght@500;600;700&display=swap" rel="stylesheet">`;

/** Typography + table primitives applied via generic-element selectors. */
export const ARTIFACT_BASE_STYLE_HTML = `<style>
  :root {
    --taqat-font-body: "Manrope", system-ui, -apple-system, "Segoe UI", sans-serif;
    --taqat-font-heading: "Sora", "Manrope", system-ui, sans-serif;
    --taqat-fg: #0f172a;
    --taqat-fg-muted: #475569;
    --taqat-surface: #ffffff;
    --taqat-border: #e2e8f0;
    --taqat-border-strong: #cbd5e1;
  }
  html, body {
    margin: 0;
    background: var(--taqat-surface);
    color: var(--taqat-fg);
    font-family: var(--taqat-font-body);
    font-size: 16px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--taqat-font-heading);
    font-weight: 600;
    color: var(--taqat-fg);
    letter-spacing: -0.01em;
    margin: 0 0 0.5em;
    line-height: 1.2;
  }
  h1 { font-size: 2rem; font-weight: 700; }
  h2 { font-size: 1.5rem; }
  h3 { font-size: 1.25rem; }
  h4 { font-size: 1.125rem; }
  h5, h6 { font-size: 1rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--taqat-fg-muted); }
  p { margin: 0 0 1em; }
  small, .caption { color: var(--taqat-fg-muted); font-size: 0.875rem; }
  strong { font-weight: 600; }
  a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }
  table {
    width: 100%;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;
    margin: 1em 0;
  }
  th, td {
    padding: 0.625em 0.75em;
    border-bottom: 1px solid var(--taqat-border);
    text-align: left;
    vertical-align: top;
  }
  thead th {
    font-family: var(--taqat-font-heading);
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--taqat-fg-muted);
    border-bottom: 1px solid var(--taqat-border-strong);
  }
  tbody tr:last-child td { border-bottom: none; }
  hr { border: 0; border-top: 1px solid var(--taqat-border); margin: 1.5em 0; }
  code, pre, kbd { font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace; }
  @page { margin: 18mm; }
</style>`;

/** Convenience — fonts + base CSS as a single string. */
export const ARTIFACT_STYLE_PRELUDE_HTML = `${ARTIFACT_FONT_LINK_HTML}
${ARTIFACT_BASE_STYLE_HTML}`;
